import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Truck, FileText, Award, ArrowRight, CheckCircle2, Mail, Phone, MessageSquare, GraduationCap, Building2, Stethoscope, FlaskConical, Users, HelpCircle } from "lucide-react";
import Layout from "@/components/Layout";
import { categories } from "@/data/categories";
import { useState, useEffect, Suspense } from "react";
import type { Product } from "@/data/types";
import ProductGrid from "@/components/ProductCard";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

// Use static path instead of ES6 import to avoid bundling
const heroImage = "/images/hero-lab.jpg";

const TrustBar = () => (
  <section className="bg-surface border-y min-h-[72px]">
    <div className="container mx-auto px-4 py-4 content-visibility-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: ShieldCheck, label: "100% Reship Guarantee", sub: "21-day tracked delivery" },
          { icon: Truck, label: "Free Shipping $500+", sub: "Worldwide delivery" },
          { icon: FileText, label: "Full Documentation", sub: "COA & SDS included" },
          { icon: Award, label: "15% Off Bitcoin", sub: "Crypto accepted" },
        ].map(({ icon: Icon, label, sub }) => (
          <div key={label} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">{label}</p>
              <p className="text-xs text-muted-foreground">{sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ProductCardLoader = () => (
  <div className="bg-card rounded-xl border border-border/60 overflow-hidden animate-pulse">
    <div className="bg-muted min-h-[140px] sm:min-h-[200px]" />
    <div className="p-4 space-y-2">
      <div className="h-4 bg-muted rounded w-3/4" />
      <div className="h-3 bg-muted rounded w-1/2" />
      <div className="h-8 bg-muted rounded w-full mt-4" />
    </div>
  </div>
);

const Index = () => {
  usePageMeta("RC Chemical Labs — Lab Chemicals & Bulk Supplies USA", "Trusted laboratory & industrial chemical supplier. Bulk raw materials, reagents, specialty chemicals. Fast delivery, ISO certified, competitive pricing.");

  const [featured, setFeatured] = useState<Product[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [email, setEmail] = useState("");

  // Lazy-load product data after initial paint
  useEffect(() => {
    const timer = typeof window !== 'undefined' && 'requestIdleCallback' in window ? window.requestIdleCallback(() => loadProducts()) : setTimeout(() => loadProducts(), 100);
    async function loadProducts() {
      const mod = await import("@/data/products");
      // Featured = Laboratory Equipment (Lab Supplies category)
      const labEquipment = mod.products.filter((p) => p.category === "Lab Supplies");
      setFeatured(labEquipment.slice(0, 4));
      setProductsLoaded(true);
    }
    return () => {
      if (typeof timer === "number") {
        if (window.cancelIdleCallback) window.cancelIdleCallback(timer);
        else clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    const homepageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Laboratory Chemicals Supplier - Bulk Chemical Distributor USA",
      "description": "RC Chemical Lab is a trusted laboratory and industrial chemical supplier offering bulk raw materials, reagents, and specialty chemicals with fast delivery.",
      "url": "https://www.rcchemicallab.com",
      "publisher": {
        "@type": "Organization",
        "name": "RC Chemical Lab",
        "url": "https://www.rcchemicallab.com"
      }
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "homepage-schema";
    script.textContent = JSON.stringify(homepageSchema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("homepage-schema");
      if (el) el.remove();
    };
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden min-h-[400px] md:min-h-[520px]">
        <div className="absolute inset-0">
          <img src="/images/hero-lab-1350.jpg" alt="Scientific laboratory with precision instruments" className="w-full h-full object-cover" fetchPriority="high" width={1920} height={1080} decoding="sync" loading="eager" srcSet="/images/hero-lab-768.jpg 768w, /images/hero-lab-1350.jpg 1350w, /images/hero-lab.jpg 1920w" sizes="100vw" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/70 to-primary/40" />
        </div>
        <div className="relative container mx-auto px-4 py-20 md:py-32">
          <div className="max-w-2xl">
            <Badge className="bg-accent/20 text-accent-foreground border-accent/30 mb-4 text-xs">
              Trusted Laboratory Chemicals Supplier Since 2003
            </Badge>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-primary-foreground mb-4 leading-tight">
              Laboratory Chemicals Supplier — Bulk & Industrial Chemical Distributor USA
            </h1>
            <p className="text-primary-foreground/80 text-lg md:text-xl mb-2 max-w-lg">
              Analytical grade chemicals, lab reagents, industrial solvents & specialty compounds
            </p>
            <p className="text-primary-foreground/70 text-sm mb-8 max-w-lg">
              ISO certified • High purity • Bulk availability • Fast shipping
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/shop">
                <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8">
                  Browse Products <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white font-semibold">
                  Request Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* About Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl font-bold mb-4">About RC Chemical Lab — Your Trusted Chemical Supplier USA</h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            RC Chemical Lab is a leading laboratory chemicals supplier and industrial chemical distributor 
            serving academic, industrial, and research communities since 2003. As a certified bulk chemical 
            supplier, we provide analytical grade chemicals, lab reagents, chemical raw materials, and specialty 
            compounds with rigorous quality control. Our ISO 9001 certified quality management and cGMP compliant 
            manufacturing partners make us a trusted research chemical supplier for laboratories and institutions worldwide.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-surface py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl font-bold mb-2">Shop by Category</h2>
            <p className="text-muted-foreground">Browse our comprehensive catalog of research-grade supplies</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/shop?category=${encodeURIComponent(cat.name)}`}
                className="group bg-card border rounded-lg p-5 text-center hover:shadow-lg hover:border-primary/40 hover:-translate-y-1 transition-all duration-200"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-200">{cat.icon}</div>
                <h3 className="font-display font-semibold text-sm mb-1 group-hover:text-primary transition-colors">{cat.name}</h3>
                <p className="text-xs text-muted-foreground">{cat.count} products</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-display text-3xl font-bold mb-2">Featured Laboratory Equipment</h2>
              <p className="text-muted-foreground">Precision instruments for analytical and research labs</p>
            </div>
            <Link to="/lab-supplies" className="text-sm font-medium text-accent hover:underline hidden md:block">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {!productsLoaded ? (
              Array.from({ length: 4 }).map((_, i) => <ProductCardLoader key={i} />)
            ) : (
              <Suspense fallback={Array.from({ length: 4 }).map((_, i) => <ProductCardLoader key={i} />)}>
                {featured.map((p) => (
                  <ProductGrid key={p.id} product={p} />
                ))}
              </Suspense>
            )}
          </div>
        </div>
      </section>


      {/* Quality & Compliance */}
      <section className="gradient-navy text-primary-foreground py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl font-bold mb-4">Quality & Compliance</h2>
              <p className="opacity-80 mb-6">
                Every product comes with comprehensive documentation, including Certificates of Analysis (COA) and Safety Data Sheets (SDS). We maintain the highest standards of quality assurance.
              </p>
              <ul className="space-y-3">
                {[
                  "Tested for purity and quality with every batch",
                  "ISO 9001:2015 certified quality management",
                  "cGMP compliant manufacturing partners",
                  "Safety documentation included with every order",
                  "Full traceability and lot tracking",
                  "Regulatory compliance across all product lines",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                    <span className="opacity-90">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Products", value: "100,000+" },
                { label: "Years Experience", value: "20+" },
                { label: "Quality Score", value: "99.8%" },
                { label: "On-Time Delivery", value: "97.5%" },
              ].map((stat) => (
                <div key={stat.label} className="bg-primary-foreground/10 rounded-lg p-5 text-center">
                  <p className="font-display text-2xl font-bold mb-1">{stat.value}</p>
                  <p className="text-xs opacity-70">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Customers We Serve */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl font-bold mb-2">Who We Serve</h2>
          <p className="text-muted-foreground">Trusted by leading institutions and organizations worldwide</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: GraduationCap, label: "Universities", desc: "Academic research departments" },
            { icon: FlaskConical, label: "Research Labs", desc: "Independent & government labs" },
            { icon: Building2, label: "Industrial Partners", desc: "Manufacturing & production" },
            { icon: Stethoscope, label: "Healthcare Organizations", desc: "Hospitals & pharma companies" },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="bg-card border rounded-lg p-6 text-center hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-display font-semibold mb-1">{label}</h3>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact / Support */}
      <section className="bg-surface py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Phone className="h-10 w-10 text-primary mx-auto mb-4" />
            <h2 className="font-display text-3xl font-bold mb-2">Talk to an Expert</h2>
            <p className="text-muted-foreground mb-8">
              Our team is ready to assist with product inquiries, bulk pricing, and technical support.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {[
                { icon: Phone, label: "Phone", value: "+1 (458) 888-9647", href: "tel:+14588889647" },
                { icon: Mail, label: "Email", value: "info@rcchemicallab.com", href: "mailto:info@rcchemicallab.com" },
                { icon: MessageSquare, label: "Signal / Telegram", value: "@chemwayusa", href: "#" },
              ].map(({ icon: Icon, label, value, href }) => (
                <a key={label} href={href} className="bg-card border rounded-lg p-5 hover:shadow-md transition-shadow block">
                  <Icon className="h-5 w-5 text-primary mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground mb-1">{label}</p>
                  <p className="text-sm font-semibold">{value}</p>
                </a>
              ))}
            </div>
            <Link to="/contact">
              <Button size="lg" className="px-8">
                Contact Us <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <HelpCircle className="h-10 w-10 text-primary mx-auto mb-4" />
            <h2 className="font-display text-3xl font-bold mb-2">Frequently Asked Questions</h2>
            <p className="text-muted-foreground">Answers to common questions about our products, ordering, and shipping.</p>
          </div>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="q1">
              <AccordionTrigger>What products does RC Chemical Lab supply?</AccordionTrigger>
              <AccordionContent>
                We supply analytical grade chemicals, lab reagents, industrial solvents, specialty research compounds, and laboratory equipment including balances, moisture analyzers, and filters. All products are intended for laboratory and research use only.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q2">
              <AccordionTrigger>Do you provide Certificates of Analysis (COA) and Safety Data Sheets (SDS)?</AccordionTrigger>
              <AccordionContent>
                Yes. Every order ships with a Certificate of Analysis and Safety Data Sheet. Our products are tested for purity with every batch and backed by ISO 9001:2015 certified quality management.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q3">
              <AccordionTrigger>What payment methods do you accept?</AccordionTrigger>
              <AccordionContent>
                We accept Bitcoin (15% discount), USDT (TRC-20, ERC-20, BEP-20), and bank transfers for orders of $1,000 or more. All transactions are processed securely.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q4">
              <AccordionTrigger>What are your shipping rates and delivery times?</AccordionTrigger>
              <AccordionContent>
                US ground shipping is $50 for regular and $100 for overnight, with free shipping on orders over $500. We offer a 100% reship guarantee on tracked deliveries that fail to arrive within 7 days. International shipping is available.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q5">
              <AccordionTrigger>Do you offer bulk pricing and discounts?</AccordionTrigger>
              <AccordionContent>
                Yes. Volume discounts are available on bulk orders across all categories. Contact our team via the bulk orders page for a custom quote tailored to your laboratory's needs.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q6">
              <AccordionTrigger>Who can purchase from RC Chemical Lab?</AccordionTrigger>
              <AccordionContent>
                Our customers include universities, research labs, healthcare organizations, and industrial partners. All buyers must be 19+ and agree that products are for laboratory use only — not for human consumption.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          <div className="text-center mt-8">
            <Link to="/faq">
              <Button variant="outline">View All FAQs <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </section>



      {/* Newsletter */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <Mail className="h-10 w-10 text-primary mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold mb-2">Stay Updated</h2>
          <p className="text-muted-foreground mb-6">Get notified about new products, technical resources, and exclusive offers for research professionals.</p>
          <form onSubmit={(e) => e.preventDefault()} className="flex gap-2 max-w-md mx-auto">
            <label htmlFor="newsletter-email" className="sr-only">Email address</label>
            <input
              id="newsletter-email"
              name="email"
              type="email"
              placeholder="Enter your email"
              aria-label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-4 py-2.5 rounded-lg border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <Button type="submit" className="shrink-0">Subscribe</Button>
          </form>
          <p className="text-[10px] text-muted-foreground mt-3">By subscribing, you agree to our privacy policy. Unsubscribe anytime.</p>
        </div>
      </section>

      {/* Compliance Footer Note */}
      <section className="border-t py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xs text-muted-foreground">
            For laboratory use only, not for human consumption. All products comply with applicable regulations. 
            ISO 9001 • cGMP certified partners.
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
